const COMMON_CREDENTIAL_FIELDS = [
  'api_key',
  'aws_access_key',
  'aws_secret_key',
  'aws_region',
  'deployment_url',
  'google_service_account_json',
];

export const MODEL_PROVIDER_CONFIG = {
  openai: {
    label: 'OpenAI',
    color: 'success',
    kind: 'llm',
    credentials: {
      create: ['api_key'],
      discover: ['api_key'],
      bulk: ['api_key'],
      update: ['api_key'],
    },
  },
  bedrock: {
    label: 'AWS Bedrock',
    color: 'warning',
    kind: 'llm',
    credentials: {
      create: ['aws_access_key', 'aws_secret_key', 'aws_region'],
      discover: ['aws_access_key', 'aws_secret_key', 'aws_region'],
      bulk: ['aws_access_key', 'aws_secret_key', 'aws_region'],
      update: ['aws_access_key', 'aws_secret_key', 'aws_region'],
    },
  },
  azure: {
    label: 'Azure OpenAI',
    color: 'primary',
    kind: 'llm',
    credentials: {
      create: ['api_key', 'deployment_url'],
      discover: ['api_key', 'deployment_url'],
      bulk: ['api_key', 'deployment_url'],
      update: ['api_key', 'deployment_url'],
    },
  },
  ollama: {
    label: 'Ollama',
    color: 'neutral',
    kind: 'llm',
    credentials: {
      create: ['deployment_url'],
      discover: ['deployment_url'],
      bulk: ['deployment_url'],
      update: ['deployment_url'],
    },
  },
  lm_studio: {
    label: 'LM Studio',
    color: 'neutral',
    kind: 'llm',
    credentials: {
      create: ['deployment_url'],
      discover: ['deployment_url'],
      bulk: ['deployment_url'],
      update: ['deployment_url'],
    },
  },
  azure_document_intelligence: {
    label: 'Azure Document Intelligence',
    color: 'primary',
    kind: 'vision',
    credentials: {
      create: ['api_key', 'deployment_url'],
      discover: ['api_key', 'deployment_url'],
      bulk: ['api_key', 'deployment_url'],
      update: ['api_key', 'deployment_url'],
    },
  },
  google_vision: {
    label: 'Google Vision',
    color: 'info',
    kind: 'vision',
    credentials: {
      create: ['google_service_account_json'],
      discover: ['google_service_account_json'],
      bulk: ['google_service_account_json'],
      update: ['google_service_account_json'],
    },
  },
};

const hasCredentialValue = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return value !== undefined && value !== null;
};

export const normalizeProviderKey = (provider = '') => {
  const normalized = String(provider).trim().toLowerCase();

  if (!normalized) {
    return '';
  }

  if (normalized === 'lm-studio' || normalized === 'lmstudio' || normalized === 'lm_studio') {
    return 'lm_studio';
  }

  return normalized.replace(/-/g, '_');
};

export const getProviderConfig = (provider = '') => MODEL_PROVIDER_CONFIG[normalizeProviderKey(provider)] || null;

export const getProviderLabel = (provider = '') => {
  const config = getProviderConfig(provider);
  if (config?.label) {
    return config.label;
  }

  return String(provider || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getProviderColor = (provider = '') => getProviderConfig(provider)?.color || 'neutral';

export const getProviderKind = (provider = '') => getProviderConfig(provider)?.kind || null;

export const getProviderCredentialFields = (provider = '', operation = 'create') => {
  const fields = getProviderConfig(provider)?.credentials?.[operation];
  if (Array.isArray(fields)) {
    return fields;
  }

  return [];
};

export const buildProviderCredentialPayload = ({
  provider = '',
  values = {},
  operation = 'create',
}) => {
  const fields = getProviderCredentialFields(provider, operation);
  const payload = {};

  const targetFields = fields.length > 0 ? fields : COMMON_CREDENTIAL_FIELDS;

  targetFields.forEach((field) => {
    const fieldValue = values[field];
    if (hasCredentialValue(fieldValue)) {
      payload[field] = fieldValue;
    }
  });

  return payload;
};

export const hasRequiredProviderCredentials = ({
  provider = '',
  values = {},
  operation = 'discover',
}) => {
  const requiredFields = getProviderCredentialFields(provider, operation);

  if (requiredFields.length === 0) {
    return false;
  }

  return requiredFields.every((field) => hasCredentialValue(values[field]));
};

export const KNOWN_PROVIDER_OPTIONS = Object.keys(MODEL_PROVIDER_CONFIG).map((provider) => ({
  value: provider,
  label: getProviderLabel(provider),
}));

